import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntervals, type IntervalsImport, type IntervalsStatus } from '../hooks/useIntervals.ts';

/** The service's own spelling (lowercase, with the TLD). */
export const INTERVALS_NAME = 'intervals.icu';

const CARD = 'rounded-lg border border-(--color-border) bg-(--color-bg-tertiary) p-4 flex flex-col gap-3';
const MUTED = 'text-xs text-(--color-text-secondary)';
const SECONDARY_BUTTON =
  'px-3 py-1.5 rounded-lg text-xs font-semibold bg-(--color-bg-secondary) text-(--color-text-secondary) border border-(--color-border) hover:border-red-400 hover:text-red-400 disabled:opacity-50 transition-all';
const PRIMARY_BUTTON =
  'self-start px-4 py-2 rounded-lg text-sm font-bold text-white bg-(--color-accent) hover:brightness-110 disabled:opacity-50 transition-all';

function Header({ badge }: Readonly<{ badge?: string }>) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-(--color-text-primary)">{INTERVALS_NAME}</h3>
      {badge && <span className={MUTED}>{badge}</span>}
    </div>
  );
}

function ConnectButton({ label }: Readonly<{ label: string }>) {
  const { t } = useTranslation();
  const { connect } = useIntervals();
  return (
    <button onClick={() => connect.mutate()} disabled={connect.isPending} className={PRIMARY_BUTTON}>
      {connect.isPending ? `${t('intervals.connecting')}…` : label}
    </button>
  );
}

function ConnectForm() {
  const { t } = useTranslation();
  return (
    <div className={CARD}>
      <Header badge={t('intervals.notConnected')} />
      <ul className={`${MUTED} list-disc pl-4 flex flex-col gap-1`}>
        <li>{t('intervals.explainRead')}</li>
        <li>{t('intervals.explainCoach')}</li>
        <li>{t('intervals.explainGarmin')}</li>
        <li>{t('intervals.explainLink')}</li>
        <li>{t('intervals.explainHistory')}</li>
      </ul>
      <ConnectButton label={t('intervals.connect', { name: INTERVALS_NAME })} />
    </div>
  );
}

function DisconnectPanel({ rides, connected }: Readonly<{ rides: number; connected: boolean }>) {
  const { t } = useTranslation();
  const { disconnect } = useIntervals();
  const [open, setOpen] = useState(false);
  const [deleteRides, setDeleteRides] = useState(!connected);
  const action = connected ? t('intervals.disconnect') : t('intervals.deleteRides');
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={`self-start ${SECONDARY_BUTTON}`}>
        {action}
      </button>
    );
  }
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex flex-col gap-2 text-xs">
      <p className="text-(--color-text-primary)">
        {connected ? t('intervals.disconnectConfirm') : t('intervals.deleteRidesConfirm', { count: rides })}
      </p>
      {connected && rides > 0 && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={deleteRides} onChange={(e) => setDeleteRides(e.target.checked)} />
          {t('intervals.alsoDeleteRides', { count: rides })}
        </label>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => disconnect.mutate(deleteRides, { onSettled: () => setOpen(false) })}
          disabled={disconnect.isPending}
          className="px-3 py-1.5 rounded-lg font-semibold bg-red-500/80 text-white disabled:opacity-50"
        >
          {action}
        </button>
        <button onClick={() => setOpen(false)} className={SECONDARY_BUTTON}>
          {t('intervals.cancel')}
        </button>
      </div>
    </div>
  );
}

function ImportProgress({ progress }: Readonly<{ progress: IntervalsImport | null | undefined }>) {
  const { t } = useTranslation();
  if (progress?.status === 'running') {
    return (
      <output className="text-(--color-accent)">
        {t('intervals.importRunning', { done: progress.done, total: progress.total })}
      </output>
    );
  }
  if (progress?.status === 'failed') {
    return <span className="text-amber-400">{t('intervals.importFailed')}</span>;
  }
  return null;
}

function LinkedDetails({ data }: Readonly<{ data: IntervalsStatus }>) {
  const { t } = useTranslation();
  const last = data.last_activity_at
    ? new Date(data.last_activity_at).toLocaleDateString()
    : t('intervals.noRidesYet');
  return (
    <div className={`${MUTED} flex flex-col gap-1`}>
      {data.athlete_id && <span>{t('intervals.athlete', { id: data.athlete_id })}</span>}
      <span>{t('intervals.lastRide', { when: last })}</span>
      <span>{t('intervals.rides', { count: data.rides ?? 0 })}</span>
      <ImportProgress progress={data.import} />
      <span>{t('intervals.garminNote')}</span>
    </div>
  );
}

function Linked({ data }: Readonly<{ data: IntervalsStatus }>) {
  const { t } = useTranslation();
  return (
    <div className={CARD}>
      <Header badge={t('intervals.connected')} />
      <LinkedDetails data={data} />
      <DisconnectPanel rides={data.rides ?? 0} connected />
    </div>
  );
}

function NeedsReconnect({ data }: Readonly<{ data: IntervalsStatus }>) {
  const { t } = useTranslation();
  return (
    <div className={CARD}>
      <Header />
      <p className="text-xs text-amber-400">{t('intervals.needsReconnect')}</p>
      <LinkedDetails data={data} />
      <ConnectButton label={t('intervals.reconnect')} />
      <DisconnectPanel rides={data.rides ?? 0} connected />
    </div>
  );
}

/** intervals.icu card for Profile → Integrations (shown when ``features.intervals``). */
export function IntervalsConnect() {
  const { t } = useTranslation();
  const { status } = useIntervals();
  const data = status.data;

  if (status.isLoading) {
    return (
      <div className={CARD}>
        <p className="text-sm text-(--color-text-secondary)">{t('intervals.loading')}</p>
      </div>
    );
  }
  if (!data?.available) return null;
  if (data.configured === false) {
    return (
      <div className={CARD}>
        <Header />
        <p className={MUTED}>{t('intervals.notConfigured')}</p>
      </div>
    );
  }
  if (data.needs_reconnect) return <NeedsReconnect data={data} />;
  if (data.connected) return <Linked data={data} />;
  return (
    <div className="flex flex-col gap-2">
      <ConnectForm />
      {(data.rides ?? 0) > 0 && <DisconnectPanel rides={data.rides ?? 0} connected={false} />}
    </div>
  );
}
